/**
 * TimeshareOwnershipTab.jsx
 * v3.15-1224 OWNERSHIP-001 — Timeshare Ownership Records.
 *
 * Informational-only ownership module. Renders inside the Client Detail
 * page under the "Ownership Information" tab (visible only when the
 * client's company is a Timeshare Exit company). Every ownership is a
 * collapsible card. No compensation logic — this file is a pure CRUD
 * surface over `/api/clients/{client_id}/ownerships`.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ChevronDown, ChevronRight, Plus, Trash2, Save, Loader2, Home, MapPin, DollarSign, FileText, X, Users } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const OWNERSHIP_TYPES = [
  { value: 'week', label: 'Week' },
  { value: 'points', label: 'Points' },
  { value: 'fractional', label: 'Fractional' },
  { value: 'travel_club', label: 'Travel Club' },
  { value: 'other', label: 'Other' },
];
const USAGE_TYPES = [
  { value: 'annual', label: 'Annual' },
  { value: 'biennial', label: 'Biennial' },
  { value: 'odd_years', label: 'Odd Years' },
  { value: 'even_years', label: 'Even Years' },
  { value: 'floating', label: 'Floating' },
  { value: 'fixed', label: 'Fixed' },
];
const PURCHASED_FROM = [
  { value: 'developer', label: 'Developer' },
  { value: 'resale', label: 'Resale' },
  { value: 'unknown', label: 'Unknown' },
];
const MORTGAGE_STATUS = [
  { value: 'current', label: 'Current' },
  { value: 'late', label: 'Late' },
  { value: 'paid_off', label: 'Paid Off' },
  { value: 'unknown', label: 'Unknown' },
];
const ACCOUNT_STATUS = [
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'foreclosure', label: 'Foreclosure' },
  { value: 'collections', label: 'Collections' },
  { value: 'unknown', label: 'Unknown' },
];
const DOC_KINDS = [
  'Deed', 'Contract', 'Maintenance Bill', 'Mortgage Statement', 'Estoppel', 'Correspondence', 'Other',
];

const fmtMoney = (v) => v == null || v === '' ? '—' : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const cap = (s) => (s ? s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—');

const SectionHeader = ({ children, icon: Icon }) => (
  <div className="flex items-center gap-2 pt-3 pb-2 border-b border-slate-200 mb-3">
    {Icon ? <Icon className="h-3.5 w-3.5 text-indigo-700" /> : null}
    <h4 className="text-[10px] font-bold uppercase tracking-widest text-indigo-800">{children}</h4>
  </div>
);

const Field = ({ label, children, testid }) => (
  <div className="space-y-1" data-testid={testid}>
    <Label className="text-[10px] uppercase tracking-wide text-slate-500">{label}</Label>
    {children}
  </div>
);

const OwnerRow = ({ owner, onChange, onRemove, testidPrefix }) => (
  <div className="grid grid-cols-12 gap-2 items-end">
    <div className="col-span-4">
      <Field label="First Name">
        <Input value={owner.first_name || ''} onChange={e => onChange({ ...owner, first_name: e.target.value })}
               className="h-8 text-xs" data-testid={`${testidPrefix}-first`} />
      </Field>
    </div>
    <div className="col-span-4">
      <Field label="Last Name">
        <Input value={owner.last_name || ''} onChange={e => onChange({ ...owner, last_name: e.target.value })}
               className="h-8 text-xs" data-testid={`${testidPrefix}-last`} />
      </Field>
    </div>
    <div className="col-span-3">
      <Field label="Relationship (opt.)">
        <Input value={owner.relationship || ''} onChange={e => onChange({ ...owner, relationship: e.target.value })}
               className="h-8 text-xs" data-testid={`${testidPrefix}-rel`} />
      </Field>
    </div>
    {onRemove && (
      <div className="col-span-1 flex justify-end pb-1">
        <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-500" onClick={onRemove}
                data-testid={`${testidPrefix}-remove`}><X className="h-3.5 w-3.5" /></Button>
      </div>
    )}
  </div>
);

const emptyOwnership = () => ({
  primary_owner: { first_name: '', last_name: '', relationship: '' },
  co_owner: null,
  additional_owners: [],
  everyone_on_deed: null,
  primary_contact_id: null,
  resort_name: '', resort_city: '', resort_state: '',
  developer: '', contract_number: '', membership_number: '', account_number: '',
  ownership_type: '', usage_type: '',
  week_number: '', unit_number: '', season: '', bedroom_size: '',
  points_owned: null,
  has_outstanding_loan: null,
  loan_current_balance: null, loan_monthly_payment: null, loan_interest_rate: null, loan_company: '',
  annual_maintenance_fees: null, annual_increase_pct: null,
  last_maintenance_paid: '', next_maintenance_due: '', special_assessments: '',
  original_purchase_date: '', original_purchase_price: null, purchased_from: '',
  mortgage_status: '', account_status: '',
  notes: '',
  documents: [],
});

const OwnershipCard = ({ initial, clientId, isNew, onSaved, onRemoved }) => {
  const [expanded, setExpanded] = useState(isNew);
  const [state, setState] = useState(() => ({ ...emptyOwnership(), ...initial }));
  const [saving, setSaving] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [newDoc, setNewDoc] = useState({ kind: 'Deed', file_name: '', url: '', note: '' });

  useEffect(() => { setState({ ...emptyOwnership(), ...initial }); }, [initial]);

  const update = useCallback((patch) => setState(s => ({ ...s, ...patch })), []);

  const summary = useMemo(() => ({
    resort_name: state.resort_name || '(No resort)',
    location: [state.resort_city, state.resort_state].filter(Boolean).join(', ') || '—',
    type: cap(state.ownership_type),
    maintenance: fmtMoney(state.annual_maintenance_fees),
    balance: fmtMoney(state.loan_current_balance),
    status: cap(state.account_status),
  }), [state]);

  const save = async () => {
    setSaving(true);
    try {
      if (isNew || !initial?.id) {
        const { data } = await api.post(`/clients/${clientId}/ownerships`, state);
        onSaved(data.ownership, /*wasNew*/ true);
        toast.success('Ownership added');
      } else {
        const { data } = await api.put(`/clients/${clientId}/ownerships/${initial.id}`, state);
        onSaved(data.ownership, /*wasNew*/ false);
        toast.success('Ownership saved');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!initial?.id) { onRemoved(null); return; }
    if (!window.confirm('Delete this ownership? This cannot be undone.')) return;
    try {
      await api.delete(`/clients/${clientId}/ownerships/${initial.id}`);
      onRemoved(initial.id);
      toast.success('Ownership deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const attachDoc = async () => {
    if (!initial?.id) { toast.error('Save the ownership first before attaching documents'); return; }
    if (!newDoc.file_name) { toast.error('File name is required'); return; }
    setAttaching(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/ownerships/${initial.id}/documents`, newDoc);
      update({ documents: [...(state.documents || []), data.document] });
      setNewDoc({ kind: 'Deed', file_name: '', url: '', note: '' });
      toast.success('Document attached');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Attach failed');
    } finally { setAttaching(false); }
  };

  const removeDoc = async (docId) => {
    try {
      await api.delete(`/clients/${clientId}/ownerships/${initial.id}/documents/${docId}`);
      update({ documents: (state.documents || []).filter(d => d.id !== docId) });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Remove failed');
    }
  };

  return (
    <Card className="border-indigo-200" data-testid={`ownership-card-${initial?.id || 'new'}`}>
      {/* Collapsed header */}
      <button
        type="button"
        onClick={() => setExpanded(x => !x)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-indigo-50/40 transition-colors"
        data-testid={`ownership-toggle-${initial?.id || 'new'}`}
      >
        {expanded ? <ChevronDown className="h-4 w-4 text-indigo-700" /> : <ChevronRight className="h-4 w-4 text-indigo-700" />}
        <div className="flex-1 grid grid-cols-6 gap-3 items-center text-xs">
          <div className="col-span-2">
            <div className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1"><Home className="h-3 w-3" /> Resort</div>
            <div className="font-semibold text-slate-800 truncate">{summary.resort_name}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> Location</div>
            <div className="text-slate-700 truncate">{summary.location}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500">Type</div>
            <div className="text-slate-700 truncate">{summary.type}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1"><DollarSign className="h-3 w-3" /> Maint.</div>
            <div className="text-slate-700">{summary.maintenance}</div>
          </div>
          <div className="flex items-center gap-2 justify-end">
            <Badge variant="outline" className="text-[9px] px-1 py-0 border-indigo-300 text-indigo-800">{summary.status}</Badge>
            {summary.balance !== '—' && <Badge variant="outline" className="text-[9px] px-1 py-0 border-rose-300 text-rose-700 bg-rose-50">Loan {summary.balance}</Badge>}
          </div>
        </div>
      </button>

      {/* Expanded editor */}
      {expanded && (
        <CardContent className="border-t border-indigo-100 bg-white/70 px-4 pb-4 space-y-2">
          {/* OWNER INFO */}
          <SectionHeader icon={Users}>Owner Information</SectionHeader>
          <div className="space-y-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-500 mb-1 block">Primary Owner</Label>
              <OwnerRow owner={state.primary_owner || {}} onChange={v => update({ primary_owner: v })} testidPrefix="owner-primary" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-500 mb-1 block">Co-Owner</Label>
              {state.co_owner ? (
                <OwnerRow owner={state.co_owner} onChange={v => update({ co_owner: v })}
                          onRemove={() => update({ co_owner: null })} testidPrefix="owner-co" />
              ) : (
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
                        onClick={() => update({ co_owner: { first_name: '', last_name: '', relationship: '' } })}
                        data-testid="add-co-owner-btn"><Plus className="h-3 w-3" /> Add Co-Owner</Button>
              )}
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-500 mb-1 block">Additional Owners</Label>
              <div className="space-y-1">
                {(state.additional_owners || []).map((o, idx) => (
                  <OwnerRow key={idx} owner={o}
                            onChange={v => update({ additional_owners: state.additional_owners.map((x, i) => i === idx ? v : x) })}
                            onRemove={() => update({ additional_owners: state.additional_owners.filter((_, i) => i !== idx) })}
                            testidPrefix={`owner-add-${idx}`} />
                ))}
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
                        onClick={() => update({ additional_owners: [...(state.additional_owners || []), { first_name: '', last_name: '', relationship: '' }] })}
                        data-testid="add-additional-owner-btn"><Plus className="h-3 w-3" /> Add Additional Owner</Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-1">
              <Field label="Is everyone listed on the deed?">
                <Select value={state.everyone_on_deed == null ? '' : (state.everyone_on_deed ? 'yes' : 'no')}
                        onValueChange={v => update({ everyone_on_deed: v === '' ? null : v === 'yes' })}>
                  <SelectTrigger className="h-8 text-xs" data-testid="everyone-on-deed"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Primary Contact ID (optional)">
                <Input value={state.primary_contact_id || ''} onChange={e => update({ primary_contact_id: e.target.value || null })}
                       className="h-8 text-xs" placeholder="user_id or client_id" data-testid="primary-contact-id" />
              </Field>
            </div>
          </div>

          {/* RESORT INFO */}
          <SectionHeader icon={Home}>Resort Information</SectionHeader>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Resort Name" testid="fld-resort-name">
              <Input value={state.resort_name || ''} onChange={e => update({ resort_name: e.target.value })} className="h-8 text-xs" list="ptc-resort-suggestions" />
            </Field>
            <Field label="City"><Input value={state.resort_city || ''} onChange={e => update({ resort_city: e.target.value })} className="h-8 text-xs" /></Field>
            <Field label="State"><Input value={state.resort_state || ''} onChange={e => update({ resort_state: e.target.value })} className="h-8 text-xs" /></Field>
            <Field label="Developer"><Input value={state.developer || ''} onChange={e => update({ developer: e.target.value })} className="h-8 text-xs" /></Field>
            <Field label="Contract Number"><Input value={state.contract_number || ''} onChange={e => update({ contract_number: e.target.value })} className="h-8 text-xs" /></Field>
            <Field label="Membership Number"><Input value={state.membership_number || ''} onChange={e => update({ membership_number: e.target.value })} className="h-8 text-xs" /></Field>
            <Field label="Account Number"><Input value={state.account_number || ''} onChange={e => update({ account_number: e.target.value })} className="h-8 text-xs" /></Field>
          </div>

          {/* OWNERSHIP DETAILS */}
          <SectionHeader>Ownership Details</SectionHeader>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Ownership Type" testid="fld-ownership-type">
              <Select value={state.ownership_type || ''} onValueChange={v => update({ ownership_type: v })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{OWNERSHIP_TYPES.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Usage Type">
              <Select value={state.usage_type || ''} onValueChange={v => update({ usage_type: v })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{USAGE_TYPES.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Week Number"><Input value={state.week_number || ''} onChange={e => update({ week_number: e.target.value })} className="h-8 text-xs" /></Field>
            <Field label="Unit Number"><Input value={state.unit_number || ''} onChange={e => update({ unit_number: e.target.value })} className="h-8 text-xs" /></Field>
            <Field label="Season"><Input value={state.season || ''} onChange={e => update({ season: e.target.value })} className="h-8 text-xs" /></Field>
            <Field label="Bedroom Size"><Input value={state.bedroom_size || ''} onChange={e => update({ bedroom_size: e.target.value })} className="h-8 text-xs" /></Field>
            <Field label="Points Owned"><Input type="number" value={state.points_owned ?? ''} onChange={e => update({ points_owned: e.target.value === '' ? null : Number(e.target.value) })} className="h-8 text-xs" /></Field>
          </div>

          {/* FINANCIAL INFO */}
          <SectionHeader icon={DollarSign}>Financial Information</SectionHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <Field label="Outstanding Loan?">
                <Select value={state.has_outstanding_loan == null ? '' : (state.has_outstanding_loan ? 'yes' : 'no')}
                        onValueChange={v => update({ has_outstanding_loan: v === '' ? null : v === 'yes' })}>
                  <SelectTrigger className="h-8 text-xs" data-testid="has-loan-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            {state.has_outstanding_loan && (
              <div className="grid grid-cols-4 gap-3">
                <Field label="Current Balance ($)"><Input type="number" value={state.loan_current_balance ?? ''} onChange={e => update({ loan_current_balance: e.target.value === '' ? null : Number(e.target.value) })} className="h-8 text-xs" /></Field>
                <Field label="Monthly Payment ($)"><Input type="number" value={state.loan_monthly_payment ?? ''} onChange={e => update({ loan_monthly_payment: e.target.value === '' ? null : Number(e.target.value) })} className="h-8 text-xs" /></Field>
                <Field label="Interest Rate (%)"><Input type="number" step="0.01" value={state.loan_interest_rate ?? ''} onChange={e => update({ loan_interest_rate: e.target.value === '' ? null : Number(e.target.value) })} className="h-8 text-xs" /></Field>
                <Field label="Loan Company"><Input value={state.loan_company || ''} onChange={e => update({ loan_company: e.target.value })} className="h-8 text-xs" /></Field>
              </div>
            )}
            <div className="grid grid-cols-4 gap-3">
              <Field label="Annual Maintenance Fees ($)"><Input type="number" value={state.annual_maintenance_fees ?? ''} onChange={e => update({ annual_maintenance_fees: e.target.value === '' ? null : Number(e.target.value) })} className="h-8 text-xs" data-testid="fld-annual-maint" /></Field>
              <Field label="Annual Increase (%)"><Input type="number" step="0.01" value={state.annual_increase_pct ?? ''} onChange={e => update({ annual_increase_pct: e.target.value === '' ? null : Number(e.target.value) })} className="h-8 text-xs" /></Field>
              <Field label="Last Maint. Paid"><Input type="date" value={state.last_maintenance_paid || ''} onChange={e => update({ last_maintenance_paid: e.target.value })} className="h-8 text-xs" /></Field>
              <Field label="Next Due Date"><Input type="date" value={state.next_maintenance_due || ''} onChange={e => update({ next_maintenance_due: e.target.value })} className="h-8 text-xs" /></Field>
            </div>
            <Field label="Special Assessments">
              <Textarea rows={2} value={state.special_assessments || ''} onChange={e => update({ special_assessments: e.target.value })} className="text-xs" />
            </Field>
          </div>

          {/* PURCHASE INFO */}
          <SectionHeader>Purchase Information</SectionHeader>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Original Purchase Date"><Input type="date" value={state.original_purchase_date || ''} onChange={e => update({ original_purchase_date: e.target.value })} className="h-8 text-xs" /></Field>
            <Field label="Original Purchase Price ($)"><Input type="number" value={state.original_purchase_price ?? ''} onChange={e => update({ original_purchase_price: e.target.value === '' ? null : Number(e.target.value) })} className="h-8 text-xs" /></Field>
            <Field label="Purchased From">
              <Select value={state.purchased_from || ''} onValueChange={v => update({ purchased_from: v })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{PURCHASED_FROM.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>

          {/* STATUS */}
          <SectionHeader>Status</SectionHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Mortgage Status">
              <Select value={state.mortgage_status || ''} onValueChange={v => update({ mortgage_status: v })}>
                <SelectTrigger className="h-8 text-xs" data-testid="fld-mortgage-status"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{MORTGAGE_STATUS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Account Status">
              <Select value={state.account_status || ''} onValueChange={v => update({ account_status: v })}>
                <SelectTrigger className="h-8 text-xs" data-testid="fld-account-status"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{ACCOUNT_STATUS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>

          {/* DOCUMENTS */}
          <SectionHeader icon={FileText}>Documents</SectionHeader>
          <div className="space-y-2">
            {(state.documents || []).length > 0 && (
              <div className="rounded border border-slate-200 divide-y">
                {state.documents.map(d => (
                  <div key={d.id} className="flex items-center gap-2 px-2 py-1.5 text-xs" data-testid={`doc-row-${d.id}`}>
                    <Badge variant="outline" className="text-[9px] px-1 py-0">{d.kind || 'Doc'}</Badge>
                    <span className="font-medium text-slate-800 truncate flex-1">{d.file_name}</span>
                    {d.url && <a href={d.url} target="_blank" rel="noreferrer" className="text-indigo-700 hover:underline">Open</a>}
                    <Button size="icon" variant="ghost" className="h-6 w-6 text-rose-500" onClick={() => removeDoc(d.id)}
                            data-testid={`doc-remove-${d.id}`}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-12 gap-2">
              <div className="col-span-2">
                <Select value={newDoc.kind} onValueChange={v => setNewDoc({ ...newDoc, kind: v })}>
                  <SelectTrigger className="h-8 text-xs" data-testid="doc-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>{DOC_KINDS.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Input className="col-span-4 h-8 text-xs" placeholder="File name" value={newDoc.file_name}
                     onChange={e => setNewDoc({ ...newDoc, file_name: e.target.value })} data-testid="doc-file-name" />
              <Input className="col-span-4 h-8 text-xs" placeholder="URL (optional)" value={newDoc.url}
                     onChange={e => setNewDoc({ ...newDoc, url: e.target.value })} data-testid="doc-url" />
              <Button className="col-span-2 h-8 text-xs gap-1" size="sm" onClick={attachDoc} disabled={attaching}
                      data-testid="doc-attach-btn">
                {attaching ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Attach
              </Button>
            </div>
          </div>

          {/* NOTES */}
          <SectionHeader>Notes</SectionHeader>
          <Textarea rows={3} value={state.notes || ''} onChange={e => update({ notes: e.target.value })}
                    className="text-xs" placeholder="Free-form notes for this ownership only…"
                    data-testid="ownership-notes" />

          {/* ACTIONS */}
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
            {!isNew && <Button variant="outline" size="sm" className="text-xs gap-1 text-rose-600 border-rose-300 hover:bg-rose-50"
                               onClick={remove} data-testid="ownership-delete-btn">
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </Button>}
            <Button size="sm" className="text-xs gap-1" onClick={save} disabled={saving} data-testid="ownership-save-btn">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              {isNew ? 'Add Ownership' : 'Save Changes'}
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

const TimeshareOwnershipTab = ({ clientId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState([]); // client-side unsaved cards

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${clientId}/ownerships`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load ownerships');
    } finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const addDraft = () => setDrafts(d => [...d, { _draft_id: `draft-${Date.now()}` }]);

  const onSaved = (saved, wasNew) => {
    if (wasNew) {
      setDrafts(d => d.slice(1));
      setItems(prev => [saved, ...prev]);
    } else {
      setItems(prev => prev.map(i => i.id === saved.id ? saved : i));
    }
  };

  const onRemoved = (id) => {
    if (id) setItems(prev => prev.filter(i => i.id !== id));
    else setDrafts(d => d.slice(0, -1));
  };

  return (
    <div className="space-y-3" data-testid="ownership-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-slate-800">Ownership Information</h3>
          <p className="text-[11px] text-slate-500">
            Master timeshare ownership record — informational only. Feeds ExitForm360, proposals, contracts, resort research, estoppel packages, and reporting.
          </p>
        </div>
        <Button size="sm" className="gap-1 text-xs" onClick={addDraft} data-testid="add-ownership-btn">
          <Plus className="h-3.5 w-3.5" /> Add Ownership
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading ownerships…
        </div>
      ) : (
        <div className="space-y-3">
          {drafts.map((d) => (
            <OwnershipCard key={d._draft_id} initial={null} isNew clientId={clientId}
                           onSaved={onSaved} onRemoved={onRemoved} />
          ))}
          {items.length === 0 && drafts.length === 0 && (
            <div className="rounded-lg border border-dashed border-slate-300 py-10 text-center text-sm text-slate-500"
                 data-testid="ownership-empty">
              No ownerships recorded yet. Click <span className="font-semibold text-indigo-700">Add Ownership</span> to create one.
            </div>
          )}
          {items.map((o) => (
            <OwnershipCard key={o.id} initial={o} isNew={false} clientId={clientId}
                           onSaved={onSaved} onRemoved={onRemoved} />
          ))}
        </div>
      )}
    </div>
  );
};

export default TimeshareOwnershipTab;
